import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@mui/material";
import MDBox from "mdComponents/MDBox";
import MDTypography from "mdComponents/MDTypography";
import { useUser } from "../context/UserContext";
import { useHostPortalContext } from "../context/HostPortalContext";
import { useAlertContext } from "../context/AlertContext";
import corporateAccountService from "../services/corporateAccountService";
import locationsService from "../services/locationsService";
import { CorporateAccountROI } from "../models/corporateAccountROI";
import LoadingSpinner from "./LoadingSpinner";
import { formatCurrency, formatNumber, formatPercent } from "../Utils";

interface CorproateROIProps {
  limitToId?: number;
  hideBusinessName?: boolean;
  title?: string;
  description?: string;
  disableClick?: boolean;
}

const CorporateROI = (props: CorproateROIProps) => {
  const { limitToId, description, title, hideBusinessName, disableClick } = props;
  const navigate = useNavigate();
  const { setAlert } = useAlertContext();
  const { userCorporateAccount } = useUser();
  const { setHost } = useHostPortalContext();

  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [roiData, setRoiData] = useState<CorporateAccountROI[]>([]);
  const [showCorporateRollup, setShowCorporateRollup] = useState(true);

  useEffect(() => {
    const loadROI = async () => {
      try {
        let data = await corporateAccountService.getCorporateROI(
          userCorporateAccount?.id
        );

        if (data) {
            if(limitToId){
                data = data.filter(a => a.host_id === limitToId);
            };
          setRoiData(data);

          // hide corporate total if only 1 location
          setShowCorporateRollup(data.filter(a => a.host_id !== -1).length > 1);
        } else {
          setAlert("error", "Error", "Unable to load ROI data");
        }
      } catch (err) {
        console.error(err);
        setAlert("error", "Error", "Error loading ROI data");
      } finally {
        setLoading(false);
      }
    };

    loadROI();
  }, []);

  const goToManageHost = async (hostId: number) => {
    if (disableClick || hostId === -1) return;

    setRedirecting(true);

    const host = await locationsService.getHostRecord(hostId);
    if (!host) {
      setRedirecting(false);
      return;
    }

    setHost(host);
    setRedirecting(false);

    navigate(`/location/${hostId}`);
    
  };

  const renderCard = (item: CorporateAccountROI) => {
    const isTotalRow = item.host_id === -1;
    if (isTotalRow && !showCorporateRollup) return null;

    return (
      <Card
        key={item.host_id}
        onClick={() => goToManageHost(item.host_id)}
        sx={{
          mb: 2,
          cursor: isTotalRow ? "default" : "pointer",
          padding: 2,
          border: isTotalRow ? "2px solid #21b2a6" : "1px solid #ddd",
          background: isTotalRow ? "#e8fffd" : "#fff",
        }}
      >
        {!hideBusinessName &&
        <MDTypography variant="h6" fontWeight="bold">
          {isTotalRow ? "Corporate Total" : item.host_name}
        </MDTypography>}

        {item.host_free_forever && (
          <MDTypography variant="caption" color="error" display="block" mt={0.5} mb={1}>
            Location is Free Forever: ROI based on minimum $9.99 hosting fee.
          </MDTypography>
        )}

        <MDBox display="flex" justifyContent="space-between" mt={1}>
          <MDBox textAlign="center">
            <MDTypography variant="h6">{formatCurrency(item.monthly_cost)}</MDTypography>
            <MDTypography variant="caption" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>Monthly Cost</MDTypography>
          </MDBox>

          <MDBox textAlign="center">
            <MDTypography variant="h6">{formatNumber(item.visits_this_month)}</MDTypography>
            <MDTypography variant="caption" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>Visits This Month</MDTypography>
          </MDBox>

          <MDBox textAlign="center">
            <MDTypography variant="h6">{formatCurrency(item.estimated_profit_month)}</MDTypography>
            <MDTypography variant="caption" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>Profit This Month</MDTypography>
          </MDBox>

          <MDBox textAlign="center">
            <MDTypography variant="h6">
              {isTotalRow || item.months_active > 1 ? formatPercent(item.roi_month, 1) : "-"}
            </MDTypography>
            <MDTypography variant="caption" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>ROI This Month</MDTypography>
          </MDBox>
        </MDBox>

        <MDBox display="flex" justifyContent="space-between" mt={2}>
          <MDBox textAlign="center">
            <MDTypography variant="h6">{item.months_active ? formatNumber(item.months_active) : "-"}</MDTypography>
            <MDTypography variant="caption" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>Months Active</MDTypography>
          </MDBox>

          <MDBox textAlign="center">
            <MDTypography variant="h6">{formatNumber(item.visits_lifetime)}</MDTypography>
            <MDTypography variant="caption" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>Total Visits</MDTypography>
          </MDBox>

          <MDBox textAlign="center">
            <MDTypography variant="h6" sx={{color: 'green'}}>{formatCurrency(item.estimated_profit_lifetime)}</MDTypography>
            <MDTypography variant="caption" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal', color: 'green',}}>Total Profit</MDTypography>
          </MDBox>

          <MDBox textAlign="center">
            <MDTypography variant="h6" sx={{color: 'white'}}>
              {isTotalRow || item.months_active > 1 ? formatPercent(item.roi_lifetime, 1) : "-"}
            </MDTypography>
            <MDTypography variant="caption" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal'}}>ROI Lifetime</MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    );
  };

  if (loading) return <LoadingSpinner title="Loading Calculations..." />;
  if (redirecting) return <LoadingSpinner title="Loading Location Portal..." />;

  return (
    <MDBox>
      <MDTypography variant="h5" fontWeight="bold">
        {title ? title : "Estimated ROI"}
      </MDTypography>
      <MDTypography variant="button" color="text" mb={1} display="block" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
        {description ? description : "Calculations are based on a $10 purchase per customer. Click a location to view more details."}
      </MDTypography>

      {roiData.map(item => renderCard(item))}
    </MDBox>
  );
};

export default CorporateROI;
